import { useState } from "react";
import { previewChoices } from "../../../api/forms";
import { ApiError } from "../../../api/client";
import { FieldLabel, TextInput } from "../_components/AdminShell";
import { formatChoicePreview } from "../../../lib/forms/remoteChoices";
import type { ChoiceItem } from "../../../lib/forms/fieldOptions";

export function ChoicesUrlFields({
  url,
  path,
  item,
  value,
  source,
  urlPlaceholder = "https://example.com/api/list",
  onUrlChange,
  onPathChange,
  onItemChange,
  onValueChange,
}: {
  url: string;
  path: string;
  item: string;
  value: string;
  /** Для зависимого списка — текущий id города / источника. */
  source?: string;
  urlPlaceholder?: string;
  onUrlChange: (value: string) => void;
  onPathChange: (value: string) => void;
  onItemChange: (value: string) => void;
  onValueChange: (value: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ChoiceItem[] | null>(null);

  const load = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Укажите ссылку");
      setPreview(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await previewChoices({
        url: trimmed,
        path,
        item,
        value,
        source,
      });
      setPreview(result.choices);
      setError(result.error);
    } catch (err) {
      setPreview(null);
      setError(
        err instanceof ApiError
          ? err.message || "Не удалось загрузить список"
          : "Не удалось загрузить список",
      );
    } finally {
      setLoading(false);
    }
  };

  const previewText = preview?.map(formatChoicePreview).filter(Boolean) ?? [];

  return (
    <div className="space-y-2">
      <div>
        <FieldLabel size="compact">Ссылка на список</FieldLabel>
        <TextInput
          size="compact"
          value={url}
          placeholder={urlPlaceholder}
          onChange={onUrlChange}
          className="font-mono text-[12px]"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <FieldLabel size="compact">path</FieldLabel>
          <TextInput
            size="compact"
            value={path}
            placeholder="data"
            onChange={onPathChange}
            className="font-mono text-[12px]"
          />
        </div>
        <div>
          <FieldLabel size="compact">item (подпись)</FieldLabel>
          <TextInput
            size="compact"
            value={item}
            placeholder="name"
            onChange={onItemChange}
            className="font-mono text-[12px]"
          />
        </div>
        <div>
          <FieldLabel size="compact">value (id)</FieldLabel>
          <TextInput
            size="compact"
            value={value}
            placeholder="id"
            onChange={onValueChange}
            className="font-mono text-[12px]"
          />
        </div>
      </div>
      <p className="font-onest text-[11px] text-slate-500">
        Без value в ответ уходит та же строка, что видит пользователь
      </p>
      <button
        type="button"
        disabled={loading || !url.trim()}
        onClick={() => void load()}
        className="rounded-lg bg-slate-100 px-2.5 py-1.5 font-onest text-xs font-medium text-slate-700 hover:bg-slate-200/80 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? "Загрузка…" : "Загрузить"}
      </button>
      {error ? (
        <p className="font-onest text-[11px] text-red-600">{error}</p>
      ) : null}
      {previewText.length > 0 ? (
        <p className="font-onest text-[11px] text-slate-500">
          {previewText.slice(0, 8).join(", ")}
          {previewText.length > 8 ? `… ещё ${previewText.length - 8}` : ""}
        </p>
      ) : preview && preview.length === 0 && !error ? (
        <p className="font-onest text-[11px] text-slate-500">Пустой список</p>
      ) : null}
    </div>
  );
}
